import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateMany } from './queryUtils';
import {
  getEmploymentRelationships,
  createEmploymentRelationship,
  updateEmploymentRelationship,
  recordEmploymentChange,
  deleteEmploymentRelationship,
  type CreateEmploymentRelationshipInput,
  type UpdateEmploymentRelationshipInput,
  type EmploymentChangeInput,
} from '../services/api/employmentRelationships';

/**
 * Phase 71 (FTE-01/FTE-02) — TanStack Query hooks for a user's employment
 * relationships. `opts.history` fetches the full timeline (71-08); `opts.enabled`
 * gates the query (used to defer the history fetch until the toggle is opened).
 */
export function useEmploymentRelationships(
  userId: number | undefined,
  opts?: { history?: boolean; enabled?: boolean }
) {
  const history = opts?.history ?? false;
  return useQuery({
    queryKey: ['employmentRelationships', userId, history ? 'history' : 'active'],
    queryFn: () => getEmploymentRelationships(userId!, history),
    enabled: (opts?.enabled ?? true) && !!userId,
  });
}

/** 71-08: record an employment change (closes prior arrangement, opens 1-2 legs). */
export function useRecordEmploymentChange(userId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmploymentChangeInput) => recordEmploymentChange(userId!, input),
    onSuccess: () => {
      invalidateMany(queryClient, [
        ['employmentRelationships', userId],
        ['vacationBalance', userId],
      ]);
    },
  });
}

/** 71-08: delete a mistaken employment relationship from history. */
export function useDeleteEmploymentRelationship(userId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteEmploymentRelationship(userId!, id),
    onSuccess: () => {
      invalidateMany(queryClient, [
        ['employmentRelationships', userId],
        ['vacationBalance', userId],
      ]);
    },
  });
}

export function useCreateEmploymentRelationship(userId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEmploymentRelationshipInput) => createEmploymentRelationship(userId!, input),
    onSuccess: () => {
      // A relationship create/edit changes the FTE the capacity/vacation
      // engines read, so invalidate both the relationships list and the
      // balance figures derived from it.
      invalidateMany(queryClient, [
        ['employmentRelationships', userId],
        ['vacationBalance', userId],
      ]);
    },
  });
}

export function useUpdateEmploymentRelationship(userId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateEmploymentRelationshipInput }) =>
      updateEmploymentRelationship(userId!, id, input),
    onSuccess: () => {
      invalidateMany(queryClient, [
        ['employmentRelationships', userId],
        ['vacationBalance', userId],
      ]);
    },
  });
}
